import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormContainer } from '../common/form/FormContainer';
import { SubmitButton } from '../common/form/SubmitButton';
import { SectionHeader } from '../common/SectionHeader';
import { Modal as QcsModal } from '@mui/material';
import { QcsModalBox } from '../common/basic/QcsModalBox';
import { LanguageSelect } from '../common/form/LanguageSelect';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { useAppSnackbar } from '../../hooks/useAppSnackbar';
import { refreshToken } from '@api/apiClient';
import { selectIdentity } from '../../store/entities/identity';
import { useAppSelector } from '../../store';
import { isRemote } from '../../utils/roles';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { userService } from '@api/services/services';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';

interface LangForm {
  lang: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export const ChangeLangModal: FC<Props> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const identity = useAppSelector(selectIdentity);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const { enqueueSuccessSnackbar } = useAppSnackbar();

  const handleSubmit = async (data: LangForm) => {
    if (!isRemote(identity)) {
      //Load and save selected language to user preference.
      const preferences = await userService.getPreferences(identity.id);

      const data2 = {
        ...preferences.data,
        language: data.lang,
      };

      await userService.updatePreferences(identity.id, data2, {}, { silentSuccess: true });

      //Get new token with new language. If user refresh page, its load with new language.
      await refreshToken();
    }

    //Change language in UI now.
    i18n.changeLanguage(data.lang);

    enqueueSuccessSnackbar(t('changeLang.changeSuccess'));
    onClose();
  };

  return (
    <QcsModal open={open} onClose={onClose}>
      <QcsModalBox>
        <SectionHeader title="changeLang.title" />
        <FormikDebounce<LangForm> initialValues={{ lang: i18n.resolvedLanguage! }} enableReinitialize={true} onSubmit={handleSubmit}>
          <Form>
            <FormContainer>
              <LanguageSelect
                name={'lang'}
                label={t('changeLang.lang')}
                languages={SUPPORTED_LANGUAGES}
                allowedLanguages={companyCustomization?.applicationLanguages}
                hideNonSelected={true}
              />

              <ShowError resource={[resources.getUserPreferences, resources.updateUserPreferences]} />
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
};
